import { useMemo, useState } from "react";
import Modal from 'react-bootstrap/Modal';
import { Col, Form } from 'react-bootstrap';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import DOMPurify from 'dompurify';
import {SetMetaData} from './../../helper/page-title';
import useInfiniteList from "../../hooks/infinite-list";
import useColumnSettings from "../../hooks/column-setting";
import InfiniteListWrapper from "../../components/wrapper/infinite-list";
import { post_api_call, downloadSampleFile } from "../../actions/action-creators/common";
import { alertResponseMessage } from "../../actions/action-creators/response";
import FormDataCouncil from "../../data/formData/esg/council-content";
import FilterFloatMaster from "../../components/form/filter/filter-float";
import {SettingDropdownUi, ClippedCellUi} from "../../components/form/master-table/ui-static";
import PointListEditor from "../../components/form/point-list-editor";
import normalizeFilters from "../../helper/filter/normalize-filter";
import EsgCouncilContentExcelModal from "../../modal/esg/council-content-excel";
import './../../assets/css/pages/esg-council-content.css';

const {
    masterTableFields,
    levelOptions,
    councilOptions,
    yesNoOptions,
    filterTableData
} = FormDataCouncil;

const PAGE_LIMIT = 50;
const MAX_TASK_LENGTH = 10000;
const HTML_TAGS = /<\/?(p|br|strong|b|em|i|ul|ol|li)\b/i;
const SANITIZE = {ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'], ALLOWED_ATTR: []};

const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// An older council task is plain text: show it in the editor as paragraphs, keeping its line breaks.
const taskToHtml = (task) => {
    const text = String(task || "");
    if (HTML_TAGS.test(text)) return text;
    return text.trim() === "" ? "" : text.trim().split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`).join("");
};

const safeHtml = (html) => DOMPurify.sanitize(taskToHtml(html), SANITIZE);

// Edit and upload of the ESG councils' individual task, guidelines (discussion guide) and learning points (T12). The content lives in
// the LMS: this screen lists it, saves one council at a time, and updates many from an Excel file.
function EsgCouncilContent() {
    SetMetaData("ESG Council Content", "", "");
    const [filterData, setFilterData] = useState({});
    const [tmpFilterData, setTmpFilterData] = useState({});
    const [refreshKey, setRefreshKey] = useState(0);
    const [excelModal, setExcelModal] = useState(false);

    // View / edit modals
    const [viewing, setViewing] = useState(null);
    const [editing, setEditing] = useState(null);
    const [task, setTask] = useState("");
    const [guidelines, setGuidelines] = useState([]);
    const [learning, setLearning] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const { columnSettings, updateColumn } = useColumnSettings({
        level: true,
        council: true,
        individual_task: true,
        discussion_guide: true,
        learn: true
    });

    const normalizedFilter = useMemo(
        () => normalizeFilters(filterData, {
            level: "id",
            council_number: "id",
            has_task: "id",
            has_guidelines: "id",
            has_learning: "id"
        }),
        [filterData]
    );

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useInfiniteList({
        queryKey: ["esg_council_content", refreshKey],
        apiCall: payload => post_api_call("esg-council-content", payload),
        limit: PAGE_LIMIT,
        extraPayload: {
            filter: normalizedFilter
        },
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.reduce((sum, page) => sum + (Array.isArray(page?.data) ? page.data.length : 0), 0);
            return loaded < (lastPage?.total ?? 0) ? allPages.length + 1 : undefined;
        }
    });

    const masterTableData = Array.isArray(data?.pages)
                            ? data.pages.flatMap(page => Array.isArray(page?.data) ? page.data : [])
                            : [];

    const openEdit = (council) => {
        setEditing(council);
        setTask(taskToHtml(council.individual_task));
        setGuidelines(council.discussion_guide?.length ? [...council.discussion_guide] : [""]);
        setLearning(council.learn?.length ? [...council.learn] : [""]);
    };

    const closeEdit = () => {
        if (!isSaving) setEditing(null);
    };

    const saveCouncil = async (e) => {
        e.preventDefault();
        if (task.length > MAX_TASK_LENGTH) {
            alertResponseMessage({alert_type: 2, message: `The individual task can be at most ${MAX_TASK_LENGTH} characters.`});
            return;
        }
        setIsSaving(true);
        const apiData = await post_api_call("esg-council-content/update", {
            level: editing.level,
            council_number: editing.council_number,
            individual_task: task,
            discussion_guide: guidelines,
            learn: learning,
        });
        setIsSaving(false);
        if (apiData?.status === 200) {
            alertResponseMessage({alert_type: apiData.changed ? 1 : 4, message: apiData.message});
            setEditing(null);
            setRefreshKey(prev => prev + 1);
        }
    };

    const renderCell = (col, row) => {
        switch (col.fetch_key) {
            case "level":
                return `Level ${row.level}`;
            case "council":
                return <>C{row.council_number}<div className="text-muted small">{row.title}</div></>;
            case "individual_task":
                return <ClippedCellUi html={taskToHtml(row.individual_task)} sanitizeConfig={SANITIZE} className="esg-content-cell" />;
            case "discussion_guide":
                return <ClippedCellUi text={(row.discussion_guide || []).join(" • ")} className="esg-content-cell" />;
            case "learn":
                return <ClippedCellUi text={(row.learn || []).join(" • ")} className="esg-content-cell" />;
            default:
                return null;
        }
    };

    return (
        <div className="row">
            {/* Excel + template (same as ESG Student Levels) */}
            <div className="col-lg-12 text-end">
                <div className="d-grid float-end">
                    <button className="btn btn-info mb-2 p-2" onClick={() => setExcelModal(true)}>Update Council Content (Excel)</button>
                    <button className="btn btn-link p-0 mb-2" onClick={(e) => downloadSampleFile(e, 'esg_council_content', 'sample/esg-council-content', 'Template-ESG-Council-Content')}>Template File</button>
                </div>
            </div>

            {/* Filter (button + floating panel) */}
            <div className="col-lg-12">
                <div className="card p-2 mb-2">
                    <FilterFloatMaster
                        title="Filter Council Content"
                        formType="filter"
                        data={tmpFilterData}
                        _isProcess={{}}
                        _updateInput= {(form_type, variable_type, input_type, field, value) => setTmpFilterData(prev => ({...prev, [field]: value}))}
                        _formData={[{label: "Filter Council Content:", display_type: 'col', fields: [...filterTableData]}]}
                        _submitForm = {(e) => {e.preventDefault(); setFilterData({...tmpFilterData});}}
                        _is_download={false}
                        selectOptions={{
                            level: {value: 'id', label: 'label', data: [...levelOptions]},
                            council_number: {value: 'id', label: 'label', data: [...councilOptions]},
                            has_task: {value: 'id', label: 'label', data: [...yesNoOptions]},
                            has_guidelines: {value: 'id', label: 'label', data: [...yesNoOptions]},
                            has_learning: {value: 'id', label: 'label', data: [...yesNoOptions]}
                        }}
                        _resetFilterForm = {(e) => {e.preventDefault(); setTmpFilterData({}); setFilterData({});}}
                    />
                </div>
            </div>

            <InfiniteListWrapper
                dataLength={masterTableData.length}
                isLoading={isLoading}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                emptyMessage="No Council found!"
            >
                <div className="col-lg-12 grid-margin stretch-card">
                    <div className="card">
                        <SettingDropdownUi
                            checkedData={columnSettings}
                            fields={masterTableFields}
                            updateColumn= {(field, value) => updateColumn(field, value)}
                        />

                        <div className="card-body table-responsive sticky-table pt-0 pe-0 ps-3">
                            <table className="table">
                                <thead>
                                <tr>
                                    {masterTableFields.map((col, i) => (
                                        columnSettings[col.field.name] && (
                                            <th key={i} className={`w-200p ${col.class_name || ""}`}>{col.title}</th>
                                        )
                                    ))}
                                    <th>Action</th>
                                </tr>
                                </thead>

                                <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="20" className="text-center p-4">
                                            <img alt="Loader" src={require("../../assets/images/Image-Loader.gif")} />
                                        </td>
                                    </tr>
                                ) : (
                                    masterTableData.length > 0
                                    ? masterTableData.map((row) => (
                                        <tr key={`${row.level}-${row.council_number}`}>
                                            {masterTableFields.map((col, i) => (
                                                columnSettings[col.field.name] && (
                                                    <td key={i} className="align-baseline">{renderCell(col, row)}</td>
                                                )
                                            ))}
                                            <td className="align-baseline">
                                                <div className="d-flex">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-behance p-1"
                                                        title={`View C${row.council_number}`}
                                                        onClick={() => setViewing(row)}
                                                    >
                                                        <i className="mdi mdi-eye"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-inverse-info ms-2 p-1"
                                                        title={`Edit C${row.council_number}`}
                                                        onClick={() => openEdit(row)}
                                                    >
                                                        <i className="mdi mdi-pencil"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                    : (
                                    <tr>
                                        <td colSpan="20">
                                            <div className="no-fellow text-center mt-4">
                                                <p className="pt-4 head">{"No Record Found!"}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    )
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </InfiniteListWrapper>

            {/* View: every detail of the level-council (same look as the Manage Learning forms: fieldset + legend) */}
            <Modal dialogClassName="modal-70w modal-dialog-scrollable" show={viewing !== null} onHide={() => setViewing(null)} size="md" className="mod-custom team-detail" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>View Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewing && (
                        <div className="scroll-div">
                            <fieldset className="ps-0 pe-0 ps-sm-2 pe-sm-2">
                                <legend>Details</legend>
                                <div className="row m-0">
                                    <Col xs={12} md={4} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                        <Form.Group className="mb-4">
                                            <Form.Label>Level</Form.Label>
                                            <div>Level {viewing.level}</div>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={8} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                        <Form.Group className="mb-4">
                                            <Form.Label>Council</Form.Label>
                                            <div>C{viewing.council_number}: {viewing.title}</div>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                        <Form.Group className="mb-4">
                                            <Form.Label>Individual Task</Form.Label>
                                            {viewing.individual_task
                                                ? <div dangerouslySetInnerHTML={{__html: safeHtml(viewing.individual_task)}} />
                                                : <div className="text-muted">None</div>}
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                        <Form.Group className="mb-4">
                                            <Form.Label>Guidelines</Form.Label>
                                            {(viewing.discussion_guide || []).length
                                                ? <ol className="esg-content-view-list">{viewing.discussion_guide.map((g, i) => <li key={i}>{g}</li>)}</ol>
                                                : <div className="text-muted">None</div>}
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                        <Form.Group className="mb-4">
                                            <Form.Label>Learning</Form.Label>
                                            {(viewing.learn || []).length
                                                ? <ul className="esg-content-view-list">{viewing.learn.map((l, i) => <li key={i}>{l}</li>)}</ul>
                                                : <div className="text-muted">None</div>}
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                        <div className="fs-12p fst-italic text-black-50">Last updated: {viewing.updated_at ? new Date(viewing.updated_at).toLocaleString() : "-"}</div>
                                    </Col>
                                </div>
                            </fieldset>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="team-detail">
                    <button className="btn btn-secondary" onClick={() => setViewing(null)}>Close</button>
                </Modal.Footer>
            </Modal>

            {/* Edit: prefilled, with the editor (same look as the Manage Learning edit form) */}
            <Modal dialogClassName="modal-70w modal-dialog-scrollable" show={editing !== null} onHide={closeEdit} size="md" className="mod-custom team-detail" backdrop="static">
                <Modal.Header closeButton={!isSaving}>
                    <Modal.Title>Update Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editing && (
                        <Form onSubmit={saveCouncil}>
                            <div className="scroll-div">
                                <fieldset className="ps-0 pe-0 ps-sm-2 pe-sm-2">
                                    <legend>Level {editing.level} · C{editing.council_number}: {editing.title}</legend>
                                    <div className="row m-0">
                                        <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                            <Form.Group className="mb-4">
                                                <Form.Label>Individual Task</Form.Label>
                                                <CKEditor
                                                    editor={ ClassicEditor }
                                                    data={task}
                                                    onChange={ ( event, editor ) => setTask(editor.getData()) }
                                                    onReady={(editor) => {
                                                        editor.editing.view.change((writer) => {
                                                            writer.setStyle("height", '200px', editor.editing.view.document.getRoot());
                                                        });
                                                    }}
                                                    config={ {
                                                        toolbar: [ 'bold', '|', 'italic', '|', 'bulletedList', '|', 'numberedList'],
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                            <Form.Group className="mb-4">
                                                <Form.Label>Guidelines</Form.Label>
                                                <PointListEditor items={guidelines} onChange={setGuidelines} addLabel="Add guide" max={30} />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} className="ps-2 pe-2 ps-sm-3 pe-sm-3">
                                            <Form.Group className="mb-4">
                                                <Form.Label>Learning</Form.Label>
                                                <PointListEditor items={learning} onChange={setLearning} addLabel="Add learning" max={20} />
                                                <div className="fs-12p fst-italic mt-2 text-black-50">Empty guidelines and learning points are removed when you save. Students see the change straight away.</div>
                                            </Form.Group>
                                        </Col>
                                    </div>
                                </fieldset>
                            </div>
                            <p className="mb-2 mt-3 text-center justify-content-center d-flex">
                                <button className="btn btn-primary bg-color-blue" type="submit" disabled={isSaving}>
                                    <span className="fs-6">{isSaving ? "Saving..." : "Update"}</span>
                                </button>
                            </p>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer className="team-detail">
                    <button className="btn btn-secondary" disabled={isSaving} onClick={closeEdit}>Close</button>
                </Modal.Footer>
            </Modal>

            <EsgCouncilContentExcelModal
                isShow={excelModal}
                _toggleModal={(e, state) => setExcelModal(state)}
                _resetListing={() => setRefreshKey(prev => prev + 1)}
            />
        </div>
    );
}

export default EsgCouncilContent;
